import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface VideoProgressEntry {
  courseClassId: string;
  watchedSeconds: number;
  lastPosition: number;
}

export type VideoProgressRecord = Record<string, VideoProgressEntry>;

/**
 * VideoProgressSyncService
 *
 * Gestiona el almacenamiento local y sincronización optimizada del progreso de vídeo.
 *
 * Estrategia:
 * - Almacenamiento: localStorage con clave `vp_pending` (Record<classId, entry>)
 * - Sincronización: batch al backend con UPSERT
 * - Sin setInterval: usa eventos del navegador (pause, ended, beforeunload, visibilitychange, online)
 * - Nunca retroceder: usa Math.max para watchedSeconds y lastPosition
 */
@Injectable({
  providedIn: 'root'
})
export class VideoProgressSyncService implements OnDestroy {
  private readonly STORAGE_KEY = 'vp_pending';
  private isOnline = true;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initGlobalListeners();
      // Sincronizar pendientes al arrancar la app
      void this.syncToBackend();
    }
  }

  /**
   * Guarda progreso en localStorage sin hacer llamadas al backend.
   * Usa Math.max para nunca retroceder en watchedSeconds.
   * Para lastPosition: usa Math.max normalmente, pero si es seek, actualiza sin Math.max.
   * @param classId ID de la clase
   * @param currentPosition Posición actual del video
   * @param watchedSeconds Segundos reproducidos
   * @param isSeek Si true, lastPosition se actualiza sin Math.max (permite retroceso)
   */
  saveToLocalStorage(classId: string, currentPosition: number, watchedSeconds: number, isSeek: boolean = false): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const pending = this.getPendingFromStorage();

      const existing = pending[classId];
      const newPosition = Math.round(currentPosition);

      pending[classId] = {
        courseClassId: classId,
        watchedSeconds: Math.max(existing?.watchedSeconds ?? 0, Math.round(watchedSeconds)),
        lastPosition: isSeek ? newPosition : Math.max(existing?.lastPosition ?? 0, newPosition)
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(pending));
    } catch (error) {
      console.error('[VideoProgressSync] Error saving to localStorage:', error);
    }
  }

  /**
   * Limpia un registro específico del localStorage.
   * Usado cuando se cambia de clase para garantizar que se elimina del caché.
   */
  clearFromLocalStorage(classId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const pending = this.getPendingFromStorage();
      if (pending[classId]) {
        delete pending[classId];
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(pending));
      }
    } catch (error) {
      console.error('[VideoProgressSync] Error clearing from localStorage:', error);
    }
  }

  /**
   * Sincroniza progreso pendiente con el backend (batch).
   * Si se proporciona classId, solo sincroniza esa clase; si no, sincroniza todo lo pendiente.
   * Devuelve las entradas que se sincronizaron con éxito (para actualizar la memoria del componente).
   */
  async syncToBackend(classId?: string): Promise<VideoProgressEntry[]> {
    if (!isPlatformBrowser(this.platformId)) return [];

    // Snapshot del estado actual de localStorage
    const snapshot = this.getPendingFromStorage();

    if (Object.keys(snapshot).length === 0) return [];

    // Si se especifica classId, verificar que exista en localStorage
    const toSync = classId ? (snapshot[classId] ? { [classId]: snapshot[classId] } : {}) : snapshot;

    if (Object.keys(toSync).length === 0) return [];

    const idsToSync = Object.keys(toSync);
    const entries = Object.values(toSync);

    try {
      await firstValueFrom(this.http.post<any>(`${environment.apiBaseUrl}/course/student/video-progress/batch`, { entries }));

      // Re-leer localStorage DESPUÉS del HTTP para evitar race condition
      const currentPending = this.getPendingFromStorage();
      for (const cid of idsToSync) {
        delete currentPending[cid];
      }
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(currentPending));

      // Devolver las entradas sincronizadas para que el componente actualice su memoria
      return entries;
    } catch (error) {
      console.error('[VideoProgressSync] Error syncing to backend:', error);
      return [];
    }
  }

  /**
   * Envía beacon con todo lo pendiente. Usado en beforeunload y visibilitychange hidden.
   * Los beacons no retornan, así que solo enviamos y esperamos lo mejor.
   */
  sendBeaconToBackend(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const pending = this.getPendingFromStorage();

    if (Object.keys(pending).length === 0) {
      return;
    }

    try {
      const entries = Object.values(pending);
      const payload = JSON.stringify({ entries });

      // Usar Blob para asegurar que Content-Type sea application/json
      const blob = new Blob([payload], { type: 'application/json' });

      // Usar sendBeacon para garantizar envío incluso al cerrar la ventana
      navigator.sendBeacon(`${environment.apiBaseUrl}/course/student/video-progress/batch`, blob);
    } catch (error) {
      console.error('[VideoProgressSync] Error sending beacon:', error);
    }
  }

  /**
   * Obtiene un clon del progreso pendiente desde localStorage.
   */
  private getPendingFromStorage(): VideoProgressRecord {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('[VideoProgressSync] Error reading from localStorage:', error);
      return {};
    }
  }

  /**
   * Inicializa listeners globales para sincronización automática.
   */
  private initGlobalListeners(): void {
    // beforeunload: sincronizar todo lo pendiente
    window.addEventListener('beforeunload', () => {
      this.sendBeaconToBackend();
    });

    // visibilitychange: sendBeacon si hidden, syncToBackend si visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.sendBeaconToBackend();
      } else {
        void this.syncToBackend();
      }
    });

    // window online: reintentar sincronización si hay pendientes
    window.addEventListener('online', () => {
      this.isOnline = true;
      void this.syncToBackend();
    });

    // window offline: marcar estado
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  ngOnDestroy(): void {
    // Sincronizar pendientes al destruir el servicio (p.e., al navegar fuera)
    this.sendBeaconToBackend();
  }
}
